import type { Passbook } from '@/shared/types';
import type { MgmProgram } from './types';

/**
 * Mecânica do passbook do ponto de vista do MGM — a mesma decisão do motor
 * do legado (e de `services/mgmProgram.js`): hasPoints+hasCashback = cashback,
 * hasPoints = pontos, senão selos. Gift card/saldo não participa.
 */
export function programOfPassbook(
  passbook: Pick<Passbook, 'hasPoints' | 'hasCashback' | 'hasGiftcard'> | null | undefined,
): MgmProgram | null {
  if (!passbook) return null;
  if (passbook.hasGiftcard) return null;
  if (passbook.hasPoints && passbook.hasCashback) return 'cashback';
  if (passbook.hasPoints) return 'points';
  return 'stamps';
}

const UNIT: Record<MgmProgram, { one: string; many: string }> = {
  stamps: { one: 'selo', many: 'selos' },
  points: { one: 'ponto', many: 'pontos' },
  cashback: { one: 'real', many: 'reais' },
};

/** "2 selos", "1 ponto", "R$ 10,00". */
export function formatReward(program: MgmProgram, amount: number): string {
  if (program === 'cashback') {
    return amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  return `${amount} ${amount === 1 ? UNIT[program].one : UNIT[program].many}`;
}

/** Rótulo do campo de quantidade no formulário. */
export function amountLabel(program: MgmProgram): string {
  if (program === 'cashback') return 'Valor de cashback (R$)';
  return program === 'points' ? 'Quantidade de pontos' : 'Quantidade de selos';
}

export function programLabel(program: MgmProgram): string {
  return program === 'cashback' ? 'Cashback' : program === 'points' ? 'Pontos' : 'Selos';
}

/**
 * Converte o que foi digitado em quantidade válida para o programa, ou null.
 * Selos/pontos inteiros ≥ 1; cashback com centavos > 0 (aceita vírgula).
 */
export function parseAmountInput(program: MgmProgram, raw: string): number | null {
  const n = Number(String(raw).trim().replace(/\./g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  if (program === 'cashback') {
    const cents = Math.round(n * 100) / 100;
    return cents > 0 ? cents : null;
  }
  return Number.isInteger(n) && n >= 1 ? n : null;
}
